import { PageShell } from '@/components/PageShell';
import { PageBanner } from '@/components/PageBanner';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export function PageSkeleton({
  maxWidth = '7xl',
  title = true,
  banner = false,
  bannerPageKey,
  bannerConfig,
  kpis = 0,
  tabs = false,
  sections = 1,
  sectionClassName = 'h-72',
  table = false,
  tableRows = 5,
  className,
  contentClassName,
  'data-testid': dataTestId = 'page-skeleton',
}) {
  return (
    <PageShell
      maxWidth={maxWidth}
      animate={false}
      className={className}
      contentClassName={contentClassName}
      data-testid={dataTestId}
    >
      {banner && (bannerPageKey || bannerConfig ? (
        <PageBanner pageKey={bannerPageKey} config={bannerConfig} className="mb-0" />
      ) : (
        <Skeleton className="min-h-[17rem] w-full rounded-2xl sm:min-h-[12rem] lg:min-h-[9rem]" />
      ))}

      {title && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-56" />
            <Skeleton className="h-4 w-full max-w-md" />
          </div>
          <Skeleton className="h-10 w-36 rounded-xl" />
        </div>
      )}

      {kpis > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: kpis }).map((_, index) => (
            <div key={index} className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <Skeleton className="h-12 w-12 shrink-0 rounded-2xl" />
                <div className="min-w-0 flex-1 space-y-3">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-9 w-20" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tabs && (
        <div className="rounded-2xl border border-border bg-card p-2 shadow-sm">
          <div className="grid gap-2 sm:grid-cols-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
        </div>
      )}

      {table && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Skeleton className="h-10 w-full lg:max-w-sm" />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Skeleton className="h-10 w-full sm:w-40" />
              <Skeleton className="h-10 w-full sm:w-40" />
            </div>
          </div>
          <div className="rounded-xl border border-border">
            <div className="grid grid-cols-4 gap-4 border-b border-border p-4">
              {[1, 2, 3, 4].map((header) => (
                <Skeleton key={header} className="h-4 w-24" />
              ))}
            </div>
            {Array.from({ length: tableRows }).map((_, index) => (
              <div key={index} className="grid grid-cols-4 gap-4 border-b border-border p-4 last:border-b-0">
                <div className="col-span-2 flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-8 w-24 rounded-xl justify-self-end" />
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-64 rounded-xl" />
          </div>
        </div>
      )}

      {!table && Array.from({ length: sections }).map((_, index) => (
        <div key={index} className={cn('rounded-xl border border-border bg-card p-5 shadow-sm', sectionClassName)}>
          <div className="space-y-2">
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="mt-6 space-y-3">
            {[1, 2, 3].map((row) => (
              <div key={row} className="flex items-center gap-3 rounded-xl bg-muted/35 p-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      ))}

      {table && Array.from({ length: sections }).map((_, index) => (
        <Skeleton key={index} className={cn('w-full rounded-xl', sectionClassName)} />
      ))}
    </PageShell>
  );
}
